import { create } from 'zustand';

import type { StudioCapabilityName } from '../../../engine/scripting/capabilityNames';
import type { HollowScriptRuntime } from '../../../engine/scripting/hollowScriptRuntime';
import type { SandboxEffect, SandboxScriptHandles } from '../../../engine/scripting/sandboxHandles';
import { buildScriptModules, scriptModulePath } from '../../../engine/scripting/scriptModules';
import { formatScriptValue } from '../scriptConsoleFormat';
import { useScriptConsoleStore } from '../scriptConsoleStore';
import type { ScriptDocument } from '../../services/scripts';

// The Code workspace's run output: what happened the last time a creator
// pressed Run. Shared across the Editor panel (the Run/Stop buttons and the
// status bar) and the Output panel (the feed), two independent dockview
// panels with no common React parent.
//
// Where a run actually executes: against the live Preview session's runtime
// when one exists (the same `HollowScriptRuntime` the Preview console uses,
// registered in `scriptConsoleStore`), else against a sandbox runtime this
// store owns - a real Worker running the real interpreter, with every
// engine handle replaced by an in-memory stand-in that reports what it
// would have done (`sandboxHandles.ts`). Preview is a separate, chrome-free
// route, so in practice a run from the Code workspace is always a sandbox
// run - which is the point: Run works the instant the workspace opens, with
// nothing else to set up, and the Output panel says plainly which kind of
// run it was.

export type OutputKind = 'run' | 'print' | 'effect' | 'result' | 'error' | 'warning' | 'sys';
export type OutputFilter = 'all' | 'print' | 'effect' | 'problem';

export interface OutputEntry {
  readonly id: number;
  readonly kind: OutputKind;
  readonly text: string;
  /** Epoch ms. */
  readonly time: number;
  readonly scriptId?: string;
  readonly scriptName?: string;
  /** 1-based, present on a diagnostic that points somewhere in the file. */
  readonly line?: number;
  readonly column?: number;
  /** Which engine surface a simulated effect would have reached. */
  readonly area?: SandboxEffect['area'];
}

export type RunMode = 'sandbox' | 'live';

export interface RunningState {
  readonly runId: string;
  readonly scriptId: string;
  readonly scriptName: string;
  readonly mode: RunMode;
  readonly startedAt: number;
}

interface CodeRunState {
  readonly entries: readonly OutputEntry[];
  readonly running: RunningState | null;
  readonly clearOnRun: boolean;
  readonly filter: OutputFilter;
  readonly search: string;
  /** The last run's script and outcome, for the status bar. */
  readonly lastRun: { readonly scriptId: string; readonly ok: boolean; readonly durationMs: number } | null;
  run: (script: ScriptDocument, roster: readonly ScriptDocument[], capabilities: ReadonlySet<StudioCapabilityName> | undefined) => Promise<void>;
  stop: () => void;
  clear: () => void;
  setFilter: (filter: OutputFilter) => void;
  setSearch: (search: string) => void;
  setClearOnRun: (clearOnRun: boolean) => void;
  append: (entry: Omit<OutputEntry, 'id' | 'time'>) => void;
  dispose: () => void;
}

/** 1-based line/column of a character offset in `source` - the tiny piece
 * of `@hollow/script`'s `Source.positionAt` this module needs, inlined so
 * the store itself stays free of the interpreter (which only the runtime's
 * own dynamic import loads). */
export function positionAtOffset(source: string, offset: number): { line: number; column: number } {
  const clamped = Math.max(0, Math.min(offset, source.length));
  let line = 1;
  let lineStart = 0;
  for (let i = 0; i < clamped; i++) {
    if (source.charCodeAt(i) === 10) {
      line += 1;
      lineStart = i + 1;
    }
  }
  return { line, column: clamped - lineStart + 1 };
}

/** Which filter bucket an entry falls in. `run`/`sys` lines always show. */
export function entryMatchesFilter(entry: OutputEntry, filter: OutputFilter): boolean {
  if (filter === 'all' || entry.kind === 'run' || entry.kind === 'sys') return true;
  if (filter === 'print') return entry.kind === 'print' || entry.kind === 'result';
  if (filter === 'effect') return entry.kind === 'effect';
  return entry.kind === 'error' || entry.kind === 'warning';
}

// Kept out of the store so a run's worker/handles never become part of the
// rendered state. The sandbox runtime is created on first use (a dynamic
// import: the interpreter and its Worker are only fetched once someone
// actually runs something) and reused across runs, `reset()` between them.
let sandbox: { runtime: HollowScriptRuntime; handles: SandboxScriptHandles } | null = null;
let sandboxLoading: Promise<{ runtime: HollowScriptRuntime; handles: SandboxScriptHandles }> | null = null;
let nextEntryId = 1;
let currentRunId: string | null = null;

async function loadSandbox(report: (effect: SandboxEffect) => void): Promise<{ runtime: HollowScriptRuntime; handles: SandboxScriptHandles }> {
  if (sandbox) return sandbox;
  if (!sandboxLoading) {
    sandboxLoading = Promise.all([import('../../../engine/scripting/hollowScriptRuntime'), import('../../../engine/scripting/sandboxHandles')]).then(
      ([runtimeModule, handlesModule]) => {
        const handles = handlesModule.createSandboxScriptHandles(report);
        sandbox = { runtime: new runtimeModule.HollowScriptRuntime(handles), handles };
        return sandbox;
      },
    );
  }
  return sandboxLoading;
}

export const useCodeRunStore = create<CodeRunState>((set, get) => {
  function append(entry: Omit<OutputEntry, 'id' | 'time'>): void {
    set((s) => ({ entries: [...s.entries, { ...entry, id: nextEntryId++, time: Date.now() }] }));
  }

  return {
    entries: [],
    running: null,
    clearOnRun: true,
    filter: 'all',
    search: '',
    lastRun: null,

    append,

    run: async (script, roster, capabilities) => {
      if (get().running) return;
      const live = useScriptConsoleStore.getState().runtime;
      const mode: RunMode = live ? 'live' : 'sandbox';
      const scriptName = scriptModulePath(script.name);
      const tag = { scriptId: script.id, scriptName: script.name };

      if (get().clearOnRun) set({ entries: [] });
      append({ kind: 'run', text: mode === 'live' ? `Running ${scriptName} in Preview` : `Running ${scriptName} in the sandbox`, ...tag });

      let runtime: HollowScriptRuntime;
      try {
        if (live) {
          runtime = live;
        } else {
          const loaded = await loadSandbox((effect) => {
            // Effects from a run that has since been stopped still belong
            // to the feed (the script did cause them before it stopped),
            // but never to a run that never started.
            if (currentRunId === null) return;
            append({ kind: 'effect', text: effect.text, area: effect.area, ...tag });
          });
          loaded.handles.reset();
          runtime = loaded.runtime;
        }
      } catch (error) {
        append({ kind: 'error', text: `Could not start the scripting runtime: ${error instanceof Error ? error.message : String(error)}`, ...tag });
        return;
      }

      const startedAt = Date.now();
      const started = runtime.startRun(script.source, {
        capabilities,
        modules: buildScriptModules(roster, script.name),
        onPrint: (text) => append({ kind: 'print', text, ...tag }),
      });
      currentRunId = started.runId;
      set({ running: { runId: started.runId, scriptId: script.id, scriptName: script.name, mode, startedAt } });

      try {
        const outcome = await started.outcome;
        const durationMs = Date.now() - startedAt;
        if (outcome.ok) {
          if (outcome.result !== undefined) append({ kind: 'result', text: formatScriptValue(outcome.result), ...tag });
          append({ kind: 'sys', text: `Finished in ${durationMs} ms`, ...tag });
        } else {
          for (const diagnostic of outcome.diagnostics) {
            const hasPosition = diagnostic.span.end > 0 || diagnostic.span.start > 0;
            const position = hasPosition ? positionAtOffset(script.source, diagnostic.span.start) : undefined;
            append({
              kind: diagnostic.severity === 'warning' ? 'warning' : 'error',
              text: diagnostic.message,
              ...tag,
              ...(position ? { line: position.line, column: position.column } : {}),
            });
          }
          append({ kind: 'sys', text: `Stopped after ${durationMs} ms`, ...tag });
        }
        set({ lastRun: { scriptId: script.id, ok: outcome.ok, durationMs } });
      } catch (error) {
        // e.g. the runtime's Worker was torn down mid-run (Preview unmounted
        // while this was in flight) - without this, the run header would be
        // the last line with no error ever shown.
        append({ kind: 'error', text: error instanceof Error ? error.message : 'Run failed.', ...tag });
        set({ lastRun: { scriptId: script.id, ok: false, durationMs: Date.now() - startedAt } });
      } finally {
        currentRunId = null;
        set({ running: null });
      }
    },

    stop: () => {
      const running = get().running;
      if (!running) return;
      const runtime = running.mode === 'live' ? useScriptConsoleStore.getState().runtime : sandbox?.runtime;
      runtime?.cancel(running.runId);
      append({ kind: 'sys', text: 'Stopping...', scriptId: running.scriptId, scriptName: running.scriptName });
    },

    clear: () => set({ entries: [] }),
    setFilter: (filter) => set({ filter }),
    setSearch: (search) => set({ search }),
    setClearOnRun: (clearOnRun) => set({ clearOnRun }),

    dispose: () => {
      sandbox?.runtime.dispose();
      sandbox = null;
      sandboxLoading = null;
      currentRunId = null;
      set({ running: null });
    },
  };
});
